/**
 * Frame export + pagination utilities.
 *
 * Frames are addressed directly by (frameId, threadId), matching the backend
 * `/frame-data/{frame_id}` endpoints. This is the single client entry point for
 * downloading a full dataset (CSV / XLSX) and for fetching rows beyond the
 * inline preview the chat stream sends.
 */
import { buildApiUrl, tenantApiPrefix } from '../config';
import { getTenantIdFromStorage } from './onboardingLocalStorage';

export type ExportFormat = 'csv' | 'tsv' | 'xlsx';

function authContext(): { token: string | null; tenantId: string | null } {
  return {
    token: localStorage.getItem('klaris_jwt'),
    tenantId: getTenantIdFromStorage(),
  };
}

/**
 * Fetch one page of rows from a frame for server-side table pagination.
 * Returns the raw row arrays (column-ordered) for the requested page.
 */
export async function fetchFramePage(
  frameId: string,
  threadId: string,
  page: number,
  pageSize: number,
): Promise<any[][]> {
  const { token, tenantId } = authContext();
  if (!tenantId) throw new Error('Not authenticated');
  if (!frameId || !threadId) return [];

  const url = buildApiUrl(
    `${tenantApiPrefix(tenantId)}/frame-data/${frameId}/rows` +
      `?page=${page}&page_size=${pageSize}&thread_id=${encodeURIComponent(threadId)}`,
  );
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch page: ${response.statusText}`);
  }
  const result = await response.json();
  return result.rows || [];
}

/**
 * Download a full frame as a file in the requested format. Streams from the
 * server (complete dataset, not the preview) and triggers a browser download.
 */
export async function downloadFrame(
  frameId: string,
  threadId: string,
  format: ExportFormat,
  baseName?: string,
): Promise<{ filename: string; rowCount: number | null }> {
  const { token, tenantId } = authContext();
  if (!tenantId) throw new Error('Not authenticated');
  if (!frameId || !threadId) throw new Error('No data available to export');

  const params = new URLSearchParams({ format, thread_id: threadId });
  if (baseName) params.append('filename', baseName);

  const url = buildApiUrl(
    `${tenantApiPrefix(tenantId)}/frame-data/${frameId}/download?${params.toString()}`,
  );
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    // Callers distinguish 410 (the session's frame is gone — nothing to retry)
    // from a transient failure, so carry the status on the error rather than
    // making them parse the message.
    const err = new Error(error.detail || 'Failed to export data') as Error & {
      status?: number;
    };
    err.status = response.status;
    throw err;
  }

  const contentDisposition = response.headers.get('Content-Disposition');
  let filename = `export.${format}`;
  if (contentDisposition) {
    const matches = contentDisposition.match(/filename=["']?([^"';]+)["']?/);
    if (matches && matches[1]) filename = matches[1];
  }
  const rowCount = response.headers.get('X-Row-Count');

  const blob = await response.blob();
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);

  return { filename, rowCount: rowCount ? parseInt(rowCount, 10) : null };
}
